// Detect obviously-broken email addresses so organizers can spot typos
// before sending magic-link invites. Only flags clear structural or
// well-known-provider typos — never returns "broken" for a plausible
// less-common domain.
//
// looksBroken("dan@gmailcom")  -> { broken: true, reason: "Missing dot in domain" }
// looksBroken("dan@gmail.con") -> { broken: true, reason: "Domain typo (.con → .com?)" }
// looksBroken("dan@foo.org")   -> { broken: false }

const KNOWN_TYPOS = [
  // domain-without-dot typos (very common paste bug)
  { re: /@gmailcom$/i, hint: "gmail.com" },
  { re: /@yahoocom$/i, hint: "yahoo.com" },
  { re: /@hotmailcom$/i, hint: "hotmail.com" },
  { re: /@outlookcom$/i, hint: "outlook.com" },
  { re: /@icloudcom$/i, hint: "icloud.com" },
  { re: /@aolcom$/i, hint: "aol.com" },
  // misspelled providers
  { re: /@gmial\./i, hint: "gmail." },
  { re: /@gmai\./i, hint: "gmail." },
  { re: /@gnail\./i, hint: "gmail." },
  { re: /@yaho\./i, hint: "yahoo." },
  { re: /@yhoo\./i, hint: "yahoo." },
  { re: /@hotmial\./i, hint: "hotmail." },
  { re: /@hotmal\./i, hint: "hotmail." },
  { re: /@outlok\./i, hint: "outlook." },
];

const TLD_TYPOS = [
  { re: /\.con$/i, hint: ".com" },
  { re: /\.cim$/i, hint: ".com" },
  { re: /\.vom$/i, hint: ".com" },
  { re: /\.comm$/i, hint: ".com" },
  { re: /\.ccm$/i, hint: ".com" },
  { re: /\.orgg$/i, hint: ".org" },
];

// Real, valid TLDs that are ALSO common .com / .net typos. We flag these
// with a soft "did you mean" hint but MUST NOT offer an auto-fix suggestion
// — many families in Colombia (.co), Cameroon (.cm), Niger (.ne), etc.
// legitimately use these domains and a bulk auto-rewrite would silently
// corrupt real contact info.
const AMBIGUOUS_TLDS = [
  { re: /\.co$/i, guess: ".com" },
  { re: /\.cm$/i, guess: ".com" },
  { re: /\.ne$/i, guess: ".net" },
];

export function looksBroken(email) {
  const e = (email || "").trim();
  if (!e) return { broken: false };
  if (!e.includes("@")) return { broken: true, reason: "No @ sign" };
  const parts = e.split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return { broken: true, reason: "Missing username or domain" };
  }
  const [, domain] = parts;
  if (!domain.includes(".")) {
    // Try to auto-insert a dot before the TLD guess (…gmailcom → …gmail.com)
    for (const t of KNOWN_TYPOS) {
      if (t.re.test(e)) {
        return {
          broken: true,
          reason: `Provider typo — did you mean ${t.hint}?`,
          suggestion: e.replace(t.re, `@${t.hint}`),
        };
      }
    }
    return { broken: true, reason: "Missing dot in domain" };
  }
  if (/\s/.test(e)) return { broken: true, reason: "Contains a space", suggestion: e.replace(/\s+/g, "") };
  if (/,/.test(e)) return { broken: true, reason: "Contains a comma", suggestion: e.replace(/,/g, "") };
  for (const t of KNOWN_TYPOS) {
    if (t.re.test(e)) {
      return {
        broken: true,
        reason: `Provider typo — did you mean ${t.hint}?`,
        suggestion: e.replace(t.re, `@${t.hint}`),
      };
    }
  }
  for (const t of TLD_TYPOS) {
    if (t.re.test(domain)) {
      const fixed = domain.replace(t.re, t.hint);
      return {
        broken: true,
        reason: `Domain typo (${domain} → ${fixed}?)`,
        suggestion: `${parts[0]}@${fixed}`,
      };
    }
  }
  // Ambiguous TLDs — flag but never auto-fix (real TLD for many countries).
  for (const t of AMBIGUOUS_TLDS) {
    if (t.re.test(domain)) {
      return {
        broken: true,
        reason: `Ambiguous TLD (${domain}) — did you mean ${domain.replace(t.re, t.guess)}? Fix manually if wrong.`,
      };
    }
  }
  // Very short TLD like "@gmail.c"
  const tld = domain.split(".").pop();
  if (tld && tld.length < 2) {
    return { broken: true, reason: "Domain ending looks incomplete" };
  }
  return { broken: false };
}

// Return the collected broken-email issues for a family row so the UI
// can label each broken address individually.
export function familyEmailIssues(family) {
  const out = [];
  const primary = looksBroken(family?.email);
  if (primary.broken) out.push({ which: "primary", email: family.email, reason: primary.reason });
  const spouse = looksBroken(family?.partner_email);
  if (spouse.broken) out.push({ which: "spouse", email: family.partner_email, reason: spouse.reason });
  return out;
}
